import React from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { connect } from 'react-redux';
import { SparklesIcon, XMarkIcon } from '@heroicons/react/24/outline';

import { addBuildItem } from '../../../../actions';

// Photo bike-ID suggestion chip (Phase 2a). Parent (BuildItemsList) only
// renders this for the owner/admin of a frameless build with images, so
// view-only traffic never polls.
const POLL_MS = 2500;
const MAX_POLLS = 12; // ~30s

const FrameSuggestionChip = (props) => {
  const { build } = props;
  const [suggestion, setSuggestion] = React.useState(null);
  const [hidden, setHidden] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const navigate = useNavigate();

  React.useEffect(() => {
    if (!build?._id) return;
    let cancelled = false;
    let interval = null;
    let polls = 0;

    // returns true when polling should stop
    const fetchOnce = async () => {
      try {
        const { data } = await axios.get(`/api/builds/${build._id}/suggestions`);
        if (cancelled) return true;
        setSuggestion(data);
        return data?.status !== 'pending';
      } catch (err) {
        return true; // 403/network — stop quietly
      }
    };

    (async () => {
      if (await fetchOnce()) return;
      interval = setInterval(async () => {
        polls += 1;
        if ((await fetchOnce()) || polls >= MAX_POLLS) {
          clearInterval(interval);
        }
      }, POLL_MS);
    })();

    return () => {
      cancelled = true;
      if (interval) clearInterval(interval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [build?._id]);

  const frame = suggestion?.status === 'done' ? suggestion.frame : null;
  if (hidden || !frame?.resolved?._make) return null;

  const { resolved } = frame;

  const respond = (action, via) =>
    axios
      .post(`/api/builds/${build._id}/suggestions/respond`, { action, via })
      .catch(() => {});

  const onConfirm = async () => {
    setBusy(true);
    // existing add-item action: POST_BUILD_ITEM refreshes build.items,
    // which flips the parent's isFrame and unmounts this chip
    await props.addBuildItem(build._id, resolved._family, resolved._category);
    await respond('accepted', 'confirm');
    setHidden(true);
  };

  const onEdit = () => {
    respond('accepted', 'edit');
    // rides the item-edit make auto-fill (#8)
    navigate(
      `/build/${build._id}/item/edit/?name=${encodeURIComponent(resolved.label)}`,
      { state: { backable: true } }
    );
  };

  const onDismiss = () => {
    respond('dismissed');
    setHidden(true);
  };

  const isFamily = resolved.level === 'family' && resolved._family;

  return (
    <div className="flex flex-col px-4 sm:px-0 my-2">
      <div className="alert flex flex-row flex-wrap items-center gap-2 py-2">
        <SparklesIcon className="h-5 w-5 shrink-0" />
        <span className="flex-1 min-w-0 text-sm">
          {isFamily ? (
            <>
              Is this frame a <span className="font-semibold">{resolved.label}</span>?
            </>
          ) : (
            <>
              This looks like a <span className="font-semibold">{resolved.label}</span> — add
              the frame?
            </>
          )}
        </span>
        <div className="flex flex-row gap-1">
          {isFamily && (
            <button
              type="button"
              className="btn btn-primary btn-xs"
              disabled={busy}
              onClick={onConfirm}
            >
              {busy ? 'Adding...' : 'Confirm'}
            </button>
          )}
          <button
            type="button"
            className="btn btn-ghost btn-xs"
            disabled={busy}
            onClick={onEdit}
          >
            Edit
          </button>
          <button
            type="button"
            className="btn btn-ghost btn-square btn-xs"
            disabled={busy}
            onClick={onDismiss}
            aria-label="Dismiss suggestion"
          >
            <XMarkIcon className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default connect(null, { addBuildItem })(FrameSuggestionChip);
